import React from "react";
import { withApollo } from "@apollo/client/react/hoc";
import { get } from "lodash";
import { detectFieldChanges, getMetaValue } from "utils/typeUtils";

import ComparisonCard from "./ComparisonCard";
import ApprovalCard from "./ApprovalCard";

// Mirrors the approval-request selection consumed here (see GetApprovalRequestQuery
// in types/graphql). Only the fields this component reads are modeled; the parent
// passes the full object. PropTypes had this as an optional `.object`.
interface MetadataItem {
  name?: string | null;
  value?: unknown;
}

interface Approval {
  correlationName?: string | null;
  metadataItems?: (MetadataItem | null)[] | null;
}

interface Props {
  approval?: Approval;
}

// Shape of the address/business-info objects built below and passed to the
// formatter helpers + detectFieldChanges.
interface AddressInfo {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
}

interface BusinessInfo {
  name: string;
  dba: string;
  ein: string;
  email: string;
  website: string;
  businessType: string;
  naicsCode: string;
  registrationState: string;
  registrationDate: string;
  birthDate: string;
}

const BusinessRegistrationUpdateApproval = ({ approval }: Props) => {
  const metadataItems = get(approval, "metadataItems", []) as MetadataItem[];
  const companyName = get(approval, "correlationName", "");

  // getMetaValue returns `unknown`; metadata values are strings (or null) and
  // companyId is rendered directly in JSX, so narrow it to a renderable type.
  const companyId = getMetaValue(metadataItems, "companyId") as string | null;

  // Business Info fields.
  // getMetaValue returns `unknown`; metadata values are strings (or null), so
  // narrow at this read boundary. Keeps the `!==`/`||` change-detection and the
  // string-typed comparison objects below well-typed without per-use casts.
  const currentName = getMetaValue(metadataItems, "Name/oldValue") as
    | string
    | null;
  const newName = getMetaValue(metadataItems, "Name/newValue") as string | null;
  const hasNameChange = currentName !== newName && (currentName || newName);

  const currentDba = getMetaValue(metadataItems, "DBA/oldValue") as
    | string
    | null;
  const newDba = getMetaValue(metadataItems, "DBA/newValue") as string | null;
  const hasDbaChange = currentDba !== newDba && (currentDba || newDba);

  const currentEin = getMetaValue(metadataItems, "Ein/oldValue") as
    | string
    | null;
  const newEin = getMetaValue(metadataItems, "Ein/newValue") as string | null;
  const hasEinChange = currentEin !== newEin && (currentEin || newEin);

  const currentEmail = getMetaValue(metadataItems, "Email/oldValue") as
    | string
    | null;
  const newEmail = getMetaValue(metadataItems, "Email/newValue") as
    | string
    | null;
  const hasEmailChange =
    currentEmail !== newEmail && (currentEmail || newEmail);

  const currentWebsite = getMetaValue(metadataItems, "Website/oldValue") as
    | string
    | null;
  const newWebsite = getMetaValue(metadataItems, "Website/newValue") as
    | string
    | null;
  const hasWebsiteChange =
    currentWebsite !== newWebsite && (currentWebsite || newWebsite);

  const currentBusinessType = getMetaValue(
    metadataItems,
    "BusinessType/oldValue"
  ) as string | null;
  const newBusinessType = getMetaValue(
    metadataItems,
    "BusinessType/newValue"
  ) as string | null;
  const hasBusinessTypeChange =
    currentBusinessType !== newBusinessType &&
    (currentBusinessType || newBusinessType);

  const currentNaicsCode = getMetaValue(metadataItems, "NaicsCode/oldValue") as
    | string
    | null;
  const newNaicsCode = getMetaValue(metadataItems, "NaicsCode/newValue") as
    | string
    | null;
  const hasNaicsCodeChange =
    currentNaicsCode !== newNaicsCode && (currentNaicsCode || newNaicsCode);

  const currentRegistrationState = getMetaValue(
    metadataItems,
    "RegistrationState/oldValue"
  ) as string | null;
  const newRegistrationState = getMetaValue(
    metadataItems,
    "RegistrationState/newValue"
  ) as string | null;
  const hasRegistrationStateChange =
    currentRegistrationState !== newRegistrationState &&
    (currentRegistrationState || newRegistrationState);

  const currentRegistrationDate = getMetaValue(
    metadataItems,
    "RegistrationDate/oldValue"
  ) as string | null;
  const newRegistrationDate = getMetaValue(
    metadataItems,
    "RegistrationDate/newValue"
  ) as string | null;
  const hasRegistrationDateChange =
    currentRegistrationDate !== newRegistrationDate &&
    (currentRegistrationDate || newRegistrationDate);

  const currentBirthDate = getMetaValue(metadataItems, "BirthDate/oldValue") as
    | string
    | null;
  const newBirthDate = getMetaValue(metadataItems, "BirthDate/newValue") as
    | string
    | null;
  const hasBirthDateChange =
    currentBirthDate !== newBirthDate && (currentBirthDate || newBirthDate);

  // Address fields (same `string | null` narrowing as the business-info reads).
  const currentAddressLine1 = getMetaValue(
    metadataItems,
    "AddressLine1/oldValue"
  ) as string | null;
  const newAddressLine1 = getMetaValue(
    metadataItems,
    "AddressLine1/newValue"
  ) as string | null;
  const hasAddressLine1Change =
    currentAddressLine1 !== newAddressLine1 &&
    (currentAddressLine1 || newAddressLine1);

  const currentAddressLine2 = getMetaValue(
    metadataItems,
    "AddressLine2/oldValue"
  ) as string | null;
  const newAddressLine2 = getMetaValue(
    metadataItems,
    "AddressLine2/newValue"
  ) as string | null;
  const hasAddressLine2Change =
    currentAddressLine2 !== newAddressLine2 &&
    (currentAddressLine2 || newAddressLine2);

  const currentAddressCity = getMetaValue(
    metadataItems,
    "AddressCity/oldValue"
  ) as string | null;
  const newAddressCity = getMetaValue(metadataItems, "AddressCity/newValue") as
    | string
    | null;
  const hasAddressCityChange =
    currentAddressCity !== newAddressCity &&
    (currentAddressCity || newAddressCity);

  const currentAddressState = getMetaValue(
    metadataItems,
    "AddressState/oldValue"
  ) as string | null;
  const newAddressState = getMetaValue(
    metadataItems,
    "AddressState/newValue"
  ) as string | null;
  const hasAddressStateChange =
    currentAddressState !== newAddressState &&
    (currentAddressState || newAddressState);

  const currentAddressPostalCode = getMetaValue(
    metadataItems,
    "AddressPostalCode/oldValue"
  ) as string | null;
  const newAddressPostalCode = getMetaValue(
    metadataItems,
    "AddressPostalCode/newValue"
  ) as string | null;
  const hasAddressPostalCodeChange =
    currentAddressPostalCode !== newAddressPostalCode &&
    (currentAddressPostalCode || newAddressPostalCode);

  // Check if any address field changed
  const hasAddressChange =
    hasAddressLine1Change ||
    hasAddressLine2Change ||
    hasAddressCityChange ||
    hasAddressStateChange ||
    hasAddressPostalCodeChange;

  // Check if any business info field changed
  const hasBusinessInfoChange =
    hasNameChange ||
    hasDbaChange ||
    hasEinChange ||
    hasEmailChange ||
    hasWebsiteChange ||
    hasBusinessTypeChange ||
    hasNaicsCodeChange ||
    hasRegistrationStateChange ||
    hasRegistrationDateChange ||
    hasBirthDateChange;

  // Build current and new address objects for comparison
  const currentAddress: AddressInfo = {
    addressLine1: currentAddressLine1 || "",
    addressLine2: currentAddressLine2 || "",
    city: currentAddressCity || "",
    state: currentAddressState || "",
    postalCode: currentAddressPostalCode || "",
  };

  const newAddress: AddressInfo = {
    addressLine1: newAddressLine1 || "",
    addressLine2: newAddressLine2 || "",
    city: newAddressCity || "",
    state: newAddressState || "",
    postalCode: newAddressPostalCode || "",
  };

  const addressFieldLabels = {
    addressLine1: "Address Line 1",
    addressLine2: "Address Line 2",
    city: "City",
    state: "State",
    postalCode: "Postal Code",
  };

  const addressChanges = hasAddressChange
    ? detectFieldChanges(currentAddress, newAddress, addressFieldLabels)
    : [];

  // Build current and new business info objects for comparison
  const currentBusinessInfo: BusinessInfo = {
    name: currentName || "",
    dba: currentDba || "",
    ein: currentEin || "",
    email: currentEmail || "",
    website: currentWebsite || "",
    businessType: currentBusinessType || "",
    naicsCode: currentNaicsCode || "",
    registrationState: currentRegistrationState || "",
    registrationDate: currentRegistrationDate || "",
    birthDate: currentBirthDate || "",
  };

  const newBusinessInfo: BusinessInfo = {
    name: newName || "",
    dba: newDba || "",
    ein: newEin || "",
    email: newEmail || "",
    website: newWebsite || "",
    businessType: newBusinessType || "",
    naicsCode: newNaicsCode || "",
    registrationState: newRegistrationState || "",
    registrationDate: newRegistrationDate || "",
    birthDate: newBirthDate || "",
  };

  const businessInfoFieldLabels = {
    name: "Business Name",
    dba: "DBA",
    ein: "EIN",
    email: "Email",
    website: "Website",
    businessType: "Business Type",
    naicsCode: "NAICS Code",
    registrationState: "Registration State",
    registrationDate: "Registration Date",
    birthDate: "Birth Date",
  };

  const businessInfoChanges = hasBusinessInfoChange
    ? detectFieldChanges(
        currentBusinessInfo,
        newBusinessInfo,
        businessInfoFieldLabels
      )
    : [];

  // Build updated fields list
  const updatedFields = [
    hasNameChange && "Business Name",
    hasDbaChange && "DBA",
    hasEinChange && "EIN",
    hasEmailChange && "Email",
    hasWebsiteChange && "Website",
    hasBusinessTypeChange && "Business Type",
    hasNaicsCodeChange && "NAICS Code",
    hasRegistrationStateChange && "Registration State",
    hasRegistrationDateChange && "Registration Date",
    hasBirthDateChange && "Birth Date",
    hasAddressLine1Change && "Address Line 1",
    hasAddressLine2Change && "Address Line 2",
    hasAddressCityChange && "City",
    hasAddressStateChange && "State",
    hasAddressPostalCodeChange && "Postal Code",
  ]
    .filter(Boolean)
    .join(", ");

  // Format address for display
  const formatAddress = (addr: AddressInfo) => {
    const parts = [
      addr.addressLine1,
      addr.addressLine2,
      [addr.city, addr.state, addr.postalCode].filter(Boolean).join(", "),
    ].filter(Boolean);
    return parts.join("\n") || "N/A";
  };

  // Format business info for display
  const formatBusinessInfo = (info: BusinessInfo) => {
    const parts = [
      info.name && `Name: ${info.name}`,
      info.dba && `DBA: ${info.dba}`,
      info.ein && `EIN: ${info.ein}`,
      info.email && `Email: ${info.email}`,
      info.website && `Website: ${info.website}`,
      info.businessType && `Type: ${info.businessType}`,
      info.naicsCode && `NAICS: ${info.naicsCode}`,
      info.registrationState && `Reg. State: ${info.registrationState}`,
      info.registrationDate && `Reg. Date: ${info.registrationDate}`,
      info.birthDate && `Birth Date: ${info.birthDate}`,
    ].filter(Boolean);
    return parts.join("\n") || "N/A";
  };

  return (
    <>
      <ApprovalCard title="Business Information">
        <div className="row">
          <div className="col-md-6">
            <p className="account-info-label">Company ID</p>
            <p className="account-info-value">{companyId || "N/A"}</p>
          </div>
          <div className="col-md-6">
            <p className="account-info-label">Company Name</p>
            <p className="account-info-value">{companyName || "N/A"}</p>
          </div>
        </div>
        {updatedFields && (
          <div className="row" style={{ marginTop: 16 }}>
            <div className="col-md-12">
              <p className="account-info-label">Fields Being Updated</p>
              <p className="account-info-value">{updatedFields}</p>
            </div>
          </div>
        )}
      </ApprovalCard>

      {hasBusinessInfoChange && (
        <ComparisonCard
          title="Business Details Comparison"
          currentLabel="Current Details"
          newChangeLabel="New Details"
          current={formatBusinessInfo(currentBusinessInfo)}
          newChange={formatBusinessInfo(newBusinessInfo)}
          changes={businessInfoChanges}
        />
      )}

      {hasAddressChange && (
        <ComparisonCard
          title="Address Comparison"
          currentLabel="Current Address"
          newChangeLabel="New Address"
          current={formatAddress(currentAddress)}
          newChange={formatAddress(newAddress)}
          changes={addressChanges}
        />
      )}
    </>
  );
};

export default withApollo<Props>(BusinessRegistrationUpdateApproval);
